import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, Row, Select, Upload, Table, Space } from "antd";

import { useAsyncAxios, utilAxiosFormWithAuth, utilAxiosWithAppToken, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import type { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { DownloadOutlined } from "@ant-design/icons";

import * as String from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Common from "../commons/common";
import * as Request from "../commons/request";
import BBSEditor from "./BBSEditor";
import moment from "moment";

const formItemLayout = {
    labelCol: {
        sm: {
            span: 5,
        },
    },
    wrapperCol: {
        sm: {
            span: 19,
        },
    },
};

function RegisterNotice({ editMode, notice, onRegisterNotice }: { editMode: string; notice?: Types.NoticeDto; onRegisterNotice?: () => void }) {
    const [form] = Form.useForm();
    const alert = useAlert();
    const [selectedNotice, setSelectedNotice] = useState<Types.NoticeDto>();
    const [noticeTitle, setNoticeTitle] = useState("");
    const [existFiles, setExistFiles] = useState<Array<any>>([]);
    const [attachFiles, setAttachFiles] = useState<Array<any>>([]);
    const [inputs, setInputs] = useState<Types.NoticeDto>();
    const [content, setContent] = useState<string>("");
    const [noticeType, setNoticeType] = useState<string>("");
    const [selectedNoticeFiles, setSelectedNoticeFiles] = useState<Array<Types.NoticeFileDto>>();
    const noticeTypes = [
        { value: "NOTICE", label: "공지사항" },
        { value: "EVENT", label: "이벤트" },
        { value: "EVENT_ANNOUNCEMENT", label: "이벤트 발표" },
    ];

    const requestAxiosGetNotice = async (noticeId: number) => {
        const response = await utilAxiosFormWithAuth().get(Request.NOTICE_URL + "/" + noticeId);

        return response.data;
    };
    const {
        loading: loadingGetNotice,
        error: errorGetNotice,
        data: resultGetNotice,
        execute: requestGetNotice,
    } = useAsyncAxios(requestAxiosGetNotice);

    useEffect(() => {
        if (resultGetNotice === null) return;
        setSelectedNotice(resultGetNotice.notice);
    }, [resultGetNotice]);

    useEffect(() => {
        if (errorGetNotice === null) return;

        console.log("errorGetNotice", errorGetNotice);
        alert.setAlert(errorGetNotice.response.data.code, "공지사항 조회 실패", errorGetNotice.response.data.message);
    }, [errorGetNotice]);

    useEffect(() => {
        if (selectedNotice === null || selectedNotice === undefined) return;
        setNoticeType(selectedNotice.noticeType);
        form.setFieldsValue(selectedNotice);
        file(selectedNotice.noticeFiles);
    }, [selectedNotice]);

    const requestAxiosAddNotice = async (data: Types.NoticeDto) => {
        //console.log("data : " + data);

        const response = await utilAxiosFormWithAuth().post(Request.NOTICE_URL, data);

        return response.data;
    };

    const {
        loading: loadingAddNotice,
        error: errorAddNotice,
        data: resultAddNotice,
        execute: requestAddNotice,
    } = useAsyncAxios(requestAxiosAddNotice);

    useEffect(() => {
        if (notice === undefined) return;
        requestGetNotice(notice.noticeId);
    }, [notice]);

    useEffect(() => {
        if (noticeType === "NOTICE" || noticeType === "EVENT_ANNOUNCEMENT") {
            form.setFieldValue("noticeStatus", "NOTICE");
        }
        if (noticeType === "EVENT") {
            form.setFieldValue("noticeStatus", "EVENT_IN_PROGRESS");
        }
    }, [noticeType]);

    useEffect(() => {
        if (resultAddNotice === null) return;

        if (onRegisterNotice) onRegisterNotice();

        alert.setAlert(AlertType.SUCCESS, "공지사항 추가 성공", "공지사항 추가를 완료하였습니다.");
    }, [resultAddNotice]);

    useEffect(() => {
        if (errorAddNotice === null) return;

        console.log("errorNotice", errorAddNotice);
        alert.setAlert(errorAddNotice.response.data.code, "공지사항 추가 실패", errorAddNotice.response.data.message);
    }, [errorAddNotice]);

    const requestAxiosUpdateNotice = async (data: any, noticeId: number) => {
        //console.log("data : " + data);

        const response = await utilAxiosFormWithAuth().put(Request.NOTICE_URL + "/" + noticeId, data);

        return response.data;
    };

    const {
        loading: loadingUpdateNotice,
        error: errorUpdateNotice,
        data: resultUpdateNotice,
        execute: requestUpdateNotice,
    } = useAsyncAxios(requestAxiosUpdateNotice);

    useEffect(() => {
        if (resultUpdateNotice === null) return;

        if (onRegisterNotice) onRegisterNotice();

        alert.setAlert(AlertType.SUCCESS, "공지사항 수정 성공", "공지사항 수정을 완료하였습니다.");
    }, [resultUpdateNotice]);

    useEffect(() => {
        if (errorUpdateNotice === null) return;

        console.log("errorUpdateNotice", errorUpdateNotice);
        alert.setAlert(errorUpdateNotice.response.data.code, "공지사항 수정 실패", errorUpdateNotice.response.data.message);
    }, [errorUpdateNotice]);

    const onFinish = async (values: any) => {
        const formData = new FormData();
        delete values["noticeFiles"];

        const data: Types.NoticeDto = {
            ...values,
            noticeFiles: selectedNoticeFiles,
        };

        formData.append("noticeDto", new Blob([JSON.stringify(data)], { type: "application/json" }));

        for (const file in attachFiles) {
            const response = await fetch(URL.createObjectURL(attachFiles[file]));
            const blob = await response.blob();
            const data = new File([blob], attachFiles[file].name, { type: blob.type });

            formData.append("noticeFiles", data);
        }

        if (editMode === "add") requestAddNotice(formData);
        else if (editMode === "update") requestUpdateNotice(formData, values.noticeId);
    };

    const handleDownloadNoticeFile = async (noticeFileId: number, noticeFileName: string) => {
        await utilAxiosWithAppToken()
            .get(Request.NOTICE_FILE_DOWNLOAD_URL + "/" + noticeFileId, { responseType: "blob" })
            .then((response) => {
                const file = new Blob([response.data]);
                const element = document.createElement("a");
                element.href = URL.createObjectURL(file);
                element.download = noticeFileName;
                element.click();
            })
            .catch((error) => {
                console.log("downloadNoticeFile error", error);
                alert.setAlert("", "파일 다운로드 실패", "파일 다운로드에 실패했습니다.");
            });
    };

    const file = (files: Types.NoticeFileDto[]) => {
        const data: UploadFile[] = [];
        files &&
            files.map((item: any) => {
                data.push({
                    uid: item.noticeFileId,
                    name: item.noticeFileName,
                });
            });

        setExistFiles(data);
    };

    const propsAttachFiles = {
        onRemove: (file: any) => {
            const index = attachFiles.indexOf(file);
            const newAttachFiles = attachFiles.slice();
            newAttachFiles.splice(index, 1);
            setAttachFiles(newAttachFiles);
        },
        beforeUpload: (file: any) => {
            setAttachFiles([...attachFiles, file]);
            return false;
        },
        fileList: attachFiles,
    };

    const columns: ColumnsType<Types.NoticeFileDto> = [
        {
            align: "center",
            title: String.noticeFileId,
            dataIndex: "noticeFileId",
            key: "noticeFileId",
            render: (_, { noticeFileId }) => {
                return noticeFileId !== undefined ? <p>{noticeFileId}</p> : "";
            },
        },
        {
            align: "center",
            title: String.noticeFileName,
            dataIndex: "noticeFileName",
            key: "noticeFileName",
            render: (_, { noticeFileId, noticeFileName }) => {
                return noticeFileName !== undefined ? (
                    <Space onClick={() => handleDownloadNoticeFile(noticeFileId, noticeFileName)} style={{ cursor: "pointer" }}>
                        <DownloadOutlined />
                        <p>{noticeFileName}</p>
                    </Space>
                ) : (
                    ""
                );
            },
        },
    ];

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: Types.NoticeFileDto[]) => {
            const list: Array<Types.NoticeFileDto> = [];
            for (let test of selectedRows) {
                test = {
                    ...test,
                    deleted: true,
                };
                list.push(test);
            }
            setSelectedNoticeFiles(list);
        },
    };

    return (
        <Form style={{ paddingTop: 20 }} form={form} onFinish={onFinish}>
            <Row>
                <Col span={24}>
                    <Form.Item
                        className="searchForm"
                        {...formItemLayout}
                        name="title"
                        label="제목"
                        rules={[
                            {
                                required: true,
                                message: "제목을 입력해 주세요.",
                            },
                        ]}
                    >
                        {editMode === "add" ? (
                            <Input placeholder={String.title} onChange={(e) => setNoticeTitle(e.target.value)} />
                        ) : (
                            <Input value={inputs?.title} onChange={(e) => setNoticeTitle(e.target.value)} />
                        )}
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="noticeId" label="ID">
                        <Input readOnly />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="hits" label="조회수">
                        <Input readOnly />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="registeredDate" label="등록일">
                        <Input defaultValue={moment().format(Common.FORMAT_DATE_TIME)} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="noticeType" label="카테고리">
                        <Select onChange={(value) => setNoticeType(value)} options={noticeTypes} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="noticeStatus" label="이벤트 상태">
                        <Select
                            disabled={noticeType !== "EVENT" ? true : false}
                            options={[
                                { value: "EVENT_IN_PROGRESS", label: "진행" },
                                { value: "EVENT_FINISHED", label: "종료" },
                                { value: "NOTICE", label: "" },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item className="searchForm" {...formItemLayout} name="fixed" label="고정">
                        <Radio.Group>
                            <Radio value={true}>○</Radio>
                            <Radio value={false}>✕</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        className="searchForm"
                        {...formItemLayout}
                        name="contents"
                        label="내용"
                        rules={[
                            {
                                required: true,
                                message: "내용을 입력해 주세요.",
                            },
                        ]}
                    >
                        {editMode === "add" ? (
                            <BBSEditor onChange={(e) => setContent(e)} hasAttachFiles={false} />
                        ) : (
                            <BBSEditor onChange={(e) => setContent(e)} value={inputs?.contents} />
                        )}
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item className="searchForm" {...formItemLayout} name="noticeFiles" label="첨부파일">
                        <Upload {...propsAttachFiles} multiple={true}>
                            <Button icon={<UploadOutlined />}>{String.attachment}</Button>
                        </Upload>
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item className="searchForm" name="noticeFiles" label="첨부파일 목록">
                        <Table
                            className="pointerTable"
                            rowSelection={{
                                type: "checkbox",
                                ...rowSelection,
                            }}
                            rowKey="noticeFileId"
                            columns={columns}
                            dataSource={selectedNotice?.noticeFiles}
                            pagination={{ position: ["bottomCenter"], showTotal: (total) => `총 ${total}개` }}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row justify="end">
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        Register
                    </Button>
                </Form.Item>
            </Row>
        </Form>
    );
}

export default RegisterNotice;
